/** @jsxImportSource @emotion/react */

import React from 'react';
import { v4 as uuid4 } from 'uuid';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';

// ############### type ###############
interface ITab {
  id: string;
  name: string;
}

interface IProps {
  tabs?: ITab[];
  selectedTabId?: string;
  onTabClick?: (id: string) => void;
  height?: string;
  children: React.ReactNode;
}

const Wrapper = styled.div<{ $height?: string }>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  ${({ $height }) => $height && `height: ${$height};`}
  padding: ${size.PAGE_PADDING};
`;

const TabContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-end;
`;

export const ContentContainer = styled.div<{ $hasTabs: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${({ $hasTabs: hasTabs }) =>
    hasTabs ? `0 ${size.BORDER_RADIUS_2} ${size.BORDER_RADIUS_2} ${size.BORDER_RADIUS_2}` : `${size.BORDER_RADIUS_2}`};
  padding: ${size.CONTENT_PADDING};
  overflow: visible;
`;

const WorkbookTemplate: React.FC<IProps> = ({ tabs = [], selectedTabId, onTabClick, height, children }) => {
  const Tabs = tabs.map((item) => {
    const isSelected = selectedTabId === item.id;
    return (
      <TextBlock
        key={uuid4()}
        text={item.name}
        design={{
          text: {
            size: 'medium',
            bold: false,
            color: isSelected ? color.WHITE_1 : color.BLACK_2,
          },
          box: {
            alignX: 'center',
            width: '90px',
            height: '25px',
            backgroundColor: isSelected ? color.BLACK_2 : color.WHITE_1,
            borderWidth: size.BORDER_WIDTH_1,
            borderColor: isSelected ? `${color.BLACK_2}` : color.GRAY_BOX_BORDER_COLOR,
            borderRadius: '5px 5px 0px 0px',
            borderDirections: ['top', 'left', 'right'],
            event: { cursor: 'pointer' },
          },
        }}
        click={{ handler: () => onTabClick && onTabClick(item.id) }}
      />
    );
  });

  return (
    <Wrapper $height={height}>
      <TabContainer>{Tabs}</TabContainer>
      <ContentContainer $hasTabs={tabs.length > 0}>{children}</ContentContainer>
    </Wrapper>
  );
};

export default WorkbookTemplate;
